import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle, CardText, Spinner } from "reactstrap";
import { Table } from "react-bootstrap";
import {
  FaHotel, FaUsers, FaBed, FaCalendarCheck,
  FaUtensils, FaRupeeSign,
} from "react-icons/fa";
import { MdPendingActions } from "react-icons/md";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAllReservationsdashboard } from "../../helpers/api.ts";
import ReservationStatusBadge      from "../../customcomponents/ReservationStatusBadge";
import SpaAppointmentStatusBadge   from "../../customcomponents/SpaAppointmentStatusBadge";
import EmployeeStatusBadge         from "../../customcomponents/EmployeeStatusBadge";
import FoodOrderStatusBadge        from "../../customcomponents/FoodOrderStatusBadge";

// ─── Revenue formatter ────────────────────────────────────────────────────
const formatRevenue = (value) => {
  const num = Number(value) || 0;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000)   return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toFixed(0)}`;
};

// ─── Normalize salary status ──────────────────────────────────────────────
// Backend returns "Paid" / "Pending" (mixed case)
// EmployeeStatusBadge expects "Paid" / "Pending" — normalize safely
const normalizeSalaryStatus = (status) => {
  if (!status) return "Pending";
  const upper = status.toString().toUpperCase();
  if (upper === "PAID")    return "Paid";
  if (upper === "OVERDUE") return "Overdue";
  return "Pending";
};

// ─── Role-based metrics ───────────────────────────────────────────────────
const getRoleBasedMetrics = (role, data) => {
  const m = data?.metrics || {};

  switch (role) {
    case "ADMIN":
    case "SUBADMIN":
      return [
        { title: "Total Hotels",      value: m.totalHotels      || 0, icon: <FaHotel color="#28a745" size="2em" /> },
        { title: "Total Users",       value: m.totalUsers       || 0, icon: <FaUsers color="#007bff" size="2em" /> },
        { title: "Active Hotels",     value: m.activeHotels     || 0, icon: <FaHotel color="#17a2b8" size="2em" /> },
        { title: "Pending Approvals", value: m.pendingApprovals || 0, icon: <MdPendingActions color="#ffc107" size="2em" /> },
      ];

    case "HOTEL_ADMIN":
      return [
        { title: "Check-ins Active",  value: m.checkIns        || 0, icon: <FaCalendarCheck color="#28a745" size="2em" /> },
        { title: "Check-outs Today",  value: m.checkOuts       || 0, icon: <FaCalendarCheck color="#dc3545" size="2em" /> },
        { title: "Available Rooms",   value: m.availableRooms  || 0, icon: <FaBed           color="#007bff" size="2em" /> },
        { title: "Pending Requests",  value: m.pendingRequests || 0, icon: <MdPendingActions color="#ffc107" size="2em" /> },
      ];

    case "RECEPTIONIST":
    case "MANAGER":
      return [
        { title: "Check-ins Active",  value: m.checkIns        || 0, icon: <FaCalendarCheck color="#28a745" size="2em" /> },
        { title: "Check-outs Today",  value: m.checkOuts       || 0, icon: <FaCalendarCheck color="#dc3545" size="2em" /> },
        { title: "Available Rooms",   value: m.availableRooms  || 0, icon: <FaBed           color="#007bff" size="2em" /> },
        { title: "Pending Requests",  value: m.pendingRequests || 0, icon: <MdPendingActions color="#ffc107" size="2em" /> },
      ];

    case "SUPERVISOR":
    case "FOOD AND BEVERAGE":
      return [
        { title: "Active Menu Items", value: m.activeMenuItems || 0, icon: <FaUtensils color="#28a745" size="2em" /> },
        {
          title: "Today's Revenue",
          value: formatRevenue(m.todayRevenue || 0),
          icon: <FaRupeeSign color="#fd7e14" size="2em" />,
          isRevenue: true,
        },
        { title: "Total Menu Items",  value: m.totalMenuItems  || 0, icon: <FaUsers color="#007bff" size="2em" /> },
        { title: "Latest Orders",     value: m.latestOrders    || 0, icon: <MdPendingActions color="#ffc107" size="2em" /> },
      ];

    case "SPA":
      return [
        { title: "Today's Appointments", value: m.todayAppointments || 0, icon: <FaCalendarCheck color="#28a745" size="2em" /> },
        { title: "Available Slots",      value: m.availableSlots    || 0, icon: <FaCalendarCheck color="#007bff" size="2em" /> },
        { title: "Total Services",       value: m.totalServices     || 0, icon: <FaUsers         color="#17a2b8" size="2em" /> },
        {
          title: "Today's Revenue",
          value: formatRevenue(m.todayRevenue || 0),
          icon: <FaRupeeSign color="#fd7e14" size="2em" />,
          isRevenue: true,
        },
      ];

    default:
      return [];
  }
};

// ─── Promise with timeout helper ──────────────────────────────────────────
const withTimeout = (promise, ms = 10000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms)
    ),
  ]);

// ─────────────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const role     = user?.role;
  const hotel_id = user?.hotel_id || user?.hotel?.hotel_id;

  useEffect(() => {
    if (role) {
      fetchDashboard();
    } else {
      setLoading(false);
    }
  }, [role]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await withTimeout(
        getAllReservationsdashboard({ role, hotel_id }),
        10000
      );

      if (response?.success) {
        setDashboardData(response.data || {});
      } else {
        setDashboardData({});
      }
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setDashboardData({});
      setError(
        err.message?.includes("timed out")
          ? "Dashboard data could not be loaded (request timed out)."
          : "Could not load dashboard data."
      );
    } finally {
      setLoading(false);
    }
  };

  const metrics = getRoleBasedMetrics(role, dashboardData);

  // ─── Reservations table ───────────────────────────────────────────────
  const renderReservationsTable = () => (
    <Card className="shadow-sm">
      <CardBody>
        <CardTitle tag="h4">Recent Reservations</CardTitle>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Guest Name</th>
                <th>Room No.</th>
                <th>Room Type</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(dashboardData?.reservations || []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">No reservations found</td>
                </tr>
              ) : (
                (dashboardData?.reservations || []).map((reservation, i) => {
                  const guestName = reservation?.guest
                    ? `${reservation.guest.first_name || ""} ${reservation.guest.last_name || ""}`.trim()
                    : "N/A";
                  // ✅ Support both old shape (room_type) and new shape (room.room_type)
                  const roomNumber = reservation?.room?.room_number || "N/A";
                  const roomType   = reservation?.room?.room_type   || reservation?.room_type || "N/A";
                  const checkIn    = reservation?.check_in_date
                    ? new Date(reservation.check_in_date).toLocaleDateString("en-GB")
                    : "N/A";
                  const checkOut   = reservation?.check_out_date
                    ? new Date(reservation.check_out_date).toLocaleDateString("en-GB")
                    : "N/A";

                  return (
                    <tr key={reservation.reservation_id || i}>
                      <td>{i + 1}</td>
                      <td>{guestName}</td>
                      <td>{roomNumber}</td>
                      <td>{roomType}</td>
                      <td>{checkIn}</td>
                      <td>{checkOut}</td>
                      <td><ReservationStatusBadge status={reservation.status} /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );

  // ─── Employees table ──────────────────────────────────────────────────
  // salaryStatus from backend: "Paid" if salary record exists this month, else "Pending"
  // normalizeSalaryStatus() ensures correct casing before passing to EmployeeStatusBadge
  const renderEmployeesTable = () => (
    <Card className="shadow-sm">
      <CardBody>
        <CardTitle tag="h4">Employees</CardTitle>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Name</th>
                <th>Role</th>
               <th>
  Salary Status

</th>
              </tr>
            </thead>
            <tbody>
              {(dashboardData?.employees || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">No employees found</td>
                </tr>
              ) : (
                (dashboardData?.employees || []).map((employee, index) => (
                  <tr key={employee.EmployeeID || index}>
                    <td>{index + 1}</td>
                    <td>{employee.name}</td>
                    <td>{employee.role}</td>
                   <td>
  <EmployeeStatusBadge status={normalizeSalaryStatus(employee.salaryStatus)} />
  {normalizeSalaryStatus(employee.salaryStatus) === "Pending" && (
    <small
      className="text-muted d-block"
      style={{ fontSize: "10px" }}
      title="No salary record found for the previous month"
    >
      ⚠️ Prev. month salary not added
    </small>
  )}
</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );

  // ─── Food orders table ────────────────────────────────────────────────
  const renderFoodOrdersTable = () => (
    <Card className="shadow-sm">
      <CardBody>
        <CardTitle tag="h4">Active Orders</CardTitle>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Order ID</th>
                <th>Customer Name</th>
                <th>Items</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(dashboardData?.orders || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">No orders found</td>
                </tr>
              ) : (
                (dashboardData?.orders || []).map((order, i) => (
                  <tr key={order.order_id || i}>
                    <td>{i + 1}</td>
                    <td>{order.order_id}</td>
                    <td>{order.customer_name}</td>
                    <td>{order.items}</td>
                    <td><FoodOrderStatusBadge status={order.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );

  // ─── Spa appointments table ───────────────────────────────────────────
  const renderSpaAppointmentsTable = () => (
    <Card className="shadow-sm">
      <CardBody>
        <CardTitle tag="h4">Today's Appointments</CardTitle>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Time</th>
                <th>Guest Name</th>
                <th>Service</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(dashboardData?.appointments || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">No appointments today</td>
                </tr>
              ) : (
                (dashboardData?.appointments || []).map((appointment, i) => (
                  <tr key={appointment.id || i}>
                    <td>{i + 1}</td>
                    <td>
                      {new Date(appointment.appointment_time).toLocaleString("en-GB", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit", hour12: true,
                      })}
                    </td>
                    <td>{appointment.name}</td>
                    <td>{appointment.SpaService?.service_name || "N/A"}</td>
                    <td><SpaAppointmentStatusBadge status={appointment.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );

  // ─── Loading state ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading dashboard...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Portal" breadcrumbItem="Dashboard" />

          {/* ── Error banner ───────────────────────────────────────────── */}
          {error && (
            <div className="alert alert-warning py-2 mb-3" role="alert">
              ⚠️ {error}
            </div>
          )}

          {/* ── Metric Cards ───────────────────────────────────────────── */}
          <Row>
            {metrics.length === 0 ? (
              <Col>
                <div className="alert alert-info">
                  No dashboard metrics configured for role: <strong>{role || "Unknown"}</strong>
                </div>
              </Col>
            ) : (
              metrics.map((metric, index) => (
                <Col md={3} key={index} className="mb-3">
                  <Card
                    className="text-center shadow-lg h-100"
                    style={{
                      borderRadius: "10px",
                      backgroundColor: metric.isRevenue ? "#fff8f0" : "#f8f9fa",
                      border: metric.isRevenue ? "1px solid #fd7e14" : "1px solid #dee2e6",
                      transition: "transform 0.3s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  >
                    <CardBody>
                      <div>{metric.icon}</div>
                      <CardTitle tag="h6" className="mt-2 mb-1">{metric.title}</CardTitle>
                      <CardText>
                        <strong style={{
                          fontSize: metric.isRevenue ? "1.3rem" : "1.5rem",
                          color:    metric.isRevenue ? "#fd7e14" : "#212529",
                        }}>
                          {metric.value}
                        </strong>
                      </CardText>
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>

          {/* ── Tables ─────────────────────────────────────────────────── */}

          {/* Reservations: ADMIN, HOTEL_ADMIN, RECEPTIONIST, MANAGER */}
          {["ADMIN", "HOTEL_ADMIN", "RECEPTIONIST", "MANAGER"].includes(role) && (
            <Row className="mt-4">
              <Col>{renderReservationsTable()}</Col>
            </Row>
          )}

          {/* Employees: ADMIN + HOTEL_ADMIN (same as old code) */}
          {["ADMIN", "HOTEL_ADMIN"].includes(role) && (
            <Row className="mt-4">
              <Col>{renderEmployeesTable()}</Col>
            </Row>
          )}

          {/* Food Orders: FOOD AND BEVERAGE + SUPERVISOR */}
          {["FOOD AND BEVERAGE", "SUPERVISOR"].includes(role) && (
            <Row className="mt-4">
              <Col>{renderFoodOrdersTable()}</Col>
            </Row>
          )}

          {/* Spa: SPA only */}
          {role === "SPA" && (
            <Row className="mt-4">
              <Col>{renderSpaAppointmentsTable()}</Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;